import { Box, Paper, Typography, Button, Chip, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSend, FiChevronRight } from "react-icons/fi";
import { MainContainer } from "../../components/container/MainContainer";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import InvitationReport from "./InvitationReport";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import AgGrid from "../../components/grid/AgGrid";
import { leftAlignColDef, centerAlignColDef, rightAlignColDef, defaultColDef, floatFormater, translationFormater } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import RefreshButton from "../../components/button/RefreshButton";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { useMemo, useRef } from "react";
import { FiX } from "react-icons/fi";

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#16a34a", "#d97706", "#dc2626"];
const avatarColor = (name = "") => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const PendingResponses = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { refresh, setRefresh } = useContextProvider();
  const [pendingReports, setPendingReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchText, setSearchText] = useState("");
  const table_ref = useRef();

  useEffect(() => {
    Supplier_Reports.get_mine_sent()
      .then((res) => res.json())
      .then((data) => {
        const pending = data.filter((r) => r?.status === "Pending");
        setPendingReports(pending);
        setFilteredReports(pending);
      });
  }, [refresh]);

  useEffect(() => {
    if (table_ref.current?.api) {
      table_ref.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const columnDefs = useMemo(() => [
    { field: "name", headerName: t("Item Name"), ...leftAlignColDef, ...defaultColDef, flex: 2 },
    { field: "receiver_name", headerName: t("Client"), ...leftAlignColDef, ...defaultColDef, flex: 1.5 },
    { field: "year", headerName: t("Year"), ...centerAlignColDef, ...defaultColDef, width: 100 },
    { field: "level", headerName: t("Level"), ...centerAlignColDef, ...defaultColDef, width: 130, ...translationFormater(t) },
    { field: "quantity", headerName: t("Quantity"), ...rightAlignColDef, ...defaultColDef, ...floatFormater, width: 120 },
    { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater, width: 130, cellStyle: { color: "#2563eb", fontWeight: 700 } },
    { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater, width: 130, cellStyle: { color: "#16a34a", fontWeight: 700 }, valueFormatter: p => `₹${p.value?.toLocaleString()}` },
    {
      headerName: t("Action"),
      ...centerAlignColDef,
      width: 120,
      cellRenderer: (params) => (
        <Button 
          size="small" 
          variant="soft" 
          onClick={() => setSelectedReport(params.data)}
          sx={{ fontSize: 11, fontWeight: 700, borderRadius: "8px", textTransform: "none", py: 0.5 }}
        >
          {t("Review")}
        </Button>
      )
    }
  ], [t]);

  if (selectedReport) {
    return (
      <MainContainer>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Button onClick={() => setSelectedReport(null)} sx={{ textTransform: "none", fontWeight: 600 }}>
            {t("Back to List")}
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {selectedReport.name}
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden" }}>
          <InvitationReport report={selectedReport} setRefresh={setRefresh} showAction={false} />
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Box sx={{ mb: 4, animation: "org-fadeInUp 0.4s ease" }}>
        <Paper variant="Table Container" sx={{ borderRadius: "12px", overflow: "hidden", border: `1px solid ${tok.border.divider}` }}>
          <TableHeaderContainer>
            <TableTitleContainer>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="Table Title">
                  {t("Pending Responses")}
                </Typography>
                <Chip 
                  label={`${pendingReports.length} ${t("pending")}`} 
                  size="small" 
                  sx={{ bgcolor: "#fff1f2", color: "#e11d48", fontWeight: 700, fontSize: 11, height: 20 }} 
                />
              </Box>
            </TableTitleContainer>
            <TableHeaderToolsContainer>
              <SearchBox text={searchText} setText={setSearchText} placeholder={t("Filter by client, product, or level...")} />
              <RefreshButton setRefresh={setRefresh} />
            </TableHeaderToolsContainer>
          </TableHeaderContainer>

          <Box sx={{ p: 0 }}>
            <AgGrid
              ref={table_ref}
              rowData={pendingReports}
              columnDefs={columnDefs}
              domLayout="autoHeight"
              pagination={true}
            />
          </Box>
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default PendingResponses;
